/**
 * English translations
 */
const en = {
  // Common
  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    clear: 'Clear',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
  },

  // Navigation
  nav: {
    brand: 'DevToolkit',
    brandSubtitle: 'Developer Tools',
    json: 'JSON Formatter',
    jsonDesc: 'Parse, format and view JSON',
    timestamp: 'Timestamp',
    timestampDesc: 'Unix timestamp converter',
    gostruct: 'Go ↔ JSON',
    gostructDesc: 'Convert between Go struct and JSON',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    systemMode: 'System',
    github: 'GitHub',
    language: 'Language',
  },

  // Footer
  footer: {
    brand: 'DevToolkit',
    subtitle: 'Developer Tools',
    github: 'GitHub',
    feedback: 'Feedback',
    copyright: '© 2025 DevToolkit',
  },

  // JSON Tool
  json: {
    // Tabs
    tabs: {
      newTab: 'New Tab',
      closeTab: 'Close Tab',
      rename: 'Rename',
      closeOthers: 'Close Other Tabs',
      closeRight: 'Close Tabs to the Right',
      closeLeft: 'Close Tabs to the Left',
      maxTabsAlert: 'Maximum {max} tabs allowed',
      confirmCloseOthers: 'Close {count} other tabs?',
      confirmCloseRight: 'Close {count} tabs to the right?',
      confirmCloseLeft: 'Close {count} tabs to the left?',
      defaultName: 'Tab {num}',
    },

    // Toolbar
    toolbar: {
      history: 'History',
      table: 'Table',
      tree: 'Tree',
      expandAll: 'Expand All',
      collapseAll: 'Collapse All',
      clear: 'Clear',
    },

    // Input area
    input: {
      title: 'Input JSON',
      placeholder: 'Paste or type JSON here...',
      expandInput: 'Expand Input',
    },

    // View
    view: {
      treeView: 'Tree View',
      tableView: 'Table View',
      emptyTree: 'Parse JSON to see tree structure',
      emptyTable: 'Parse JSON to see table',
      noArrayData: 'No array data available for table view',
      items: 'items',
      root: 'Root',
    },

    // Tree nodes
    tree: {
      object: 'Object',
      array: 'Array',
      items: '{count} items',
      properties: '{count} properties',
      empty: 'empty',
      innerJson: 'Inner JSON',
      parseInner: 'Parse Inner',
      copyValue: 'Copy Value',
      copyPath: 'Copy Path',
    },

    // Table
    table: {
      index: 'Index',
      key: 'Key',
      value: 'Value',
      type: 'Type',
      expand: 'Expand',
      collapse: 'Collapse',
      nestedTable: 'Nested Table',
      viewDetails: 'View Details',
    },

    // Error panel
    error: {
      parseError: 'Parse Error',
      suggestion: 'Suggestion',
      fixAndRetry: 'Auto Fix',
      commonErrors: {
        unexpectedToken: 'Unexpected token',
        unterminatedString: 'Unterminated string',
        invalidNumber: 'Invalid number format',
        trailingComma: 'Trailing comma',
        singleQuotes: 'Single quotes used',
        missingQuotes: 'Missing quotes on key',
        invalidEscape: 'Invalid escape character',
      },
    },

    // History
    history: {
      title: 'History',
      empty: 'No history yet',
      clearAll: 'Clear All',
      confirmClear: 'Clear all history records?',
      loadRecord: 'Load this record',
      deleteRecord: 'Delete this record',
      timeAgo: {
        justNow: 'Just now',
        minutesAgo: '{count} min ago',
        hoursAgo: '{count} hr ago',
        daysAgo: '{count} days ago',
      },
    },

    // Features
    features: {
      title: 'Features',
      tips: 'Tips',
      highlights: 'Highlights',
      innerJson: {
        title: 'Inner JSON Parsing',
        desc: 'Automatically detect and parse nested JSON strings, commonly found in logs and API responses.',
        short: 'Nested JSON strings are auto-detected and expanded',
      },
      tableView: {
        title: 'Table View',
        desc: 'Display object arrays as tables with nested table support, browse data like Excel.',
        short: 'Browse array data like Excel, supports nesting',
      },
      nodeDetail: {
        title: 'Node Details',
        desc: 'Click any node to view full value when input is collapsed, great for long text or complex objects.',
        short: 'Click node to view full value when input collapsed',
      },
      multiTab: {
        title: 'Multi-Tab',
        desc: 'Open multiple JSON instances simultaneously, right-click tabs for quick management.',
        short: 'Handle multiple JSON files, right-click to manage tabs',
      },
      historyRecord: {
        title: 'History',
        desc: 'Click the History button to view and restore previously parsed JSON.',
        short: 'Auto-save parse history, restore anytime',
      },
      smartFix: {
        title: 'Smart Fix',
        desc: 'Get fix suggestions when parsing fails, one-click repair for common format issues.',
      },
      doubleClickRename: {
        title: 'Double-click Tab',
        desc: 'Rename tabs to distinguish different JSON data.',
      },
      middleClickClose: {
        title: 'Middle-click Close',
        desc: 'Middle-click tab to close quickly (standard browser behavior).',
      },
    },

    // Auto naming
    autoName: {
      apiResponse: 'API Response',
      listItems: 'List ({count} items)',
      userData: 'User Data',
      orderData: 'Order Data',
      productData: 'Product Data',
      listData: 'List Data',
    },
  },

  // Timestamp Tool
  timestamp: {
    title: 'Timestamp Converter',
    currentTime: 'Current Time',
    timestamp: 'Timestamp',
    datetime: 'Date Time',
    convert: 'Convert',
    swap: 'Swap',

    input: {
      timestampPlaceholder: 'Enter timestamp...',
      datetimePlaceholder: 'Enter date time...',
      selectTimezone: 'Select timezone',
    },

    output: {
      seconds: 'Seconds',
      milliseconds: 'Milliseconds',
      localTime: 'Local Time',
      utcTime: 'UTC Time',
      iso8601: 'ISO 8601',
      relative: 'Relative Time',
    },

    unit: {
      auto: 'Auto Detect',
      seconds: 'Seconds (s)',
      milliseconds: 'Milliseconds (ms)',
    },

    format: {
      title: 'Date Format',
      custom: 'Custom Format',
    },

    timezone: {
      local: 'Local Timezone',
      utc: 'UTC',
    },

    relative: {
      now: 'now',
      secondsAgo: '{count} seconds ago',
      minutesAgo: '{count} minutes ago',
      hoursAgo: '{count} hours ago',
      daysAgo: '{count} days ago',
      weeksAgo: '{count} weeks ago',
      monthsAgo: '{count} months ago',
      yearsAgo: '{count} years ago',
      secondsLater: 'in {count} seconds',
      minutesLater: 'in {count} minutes',
      hoursLater: 'in {count} hours',
      daysLater: 'in {count} days',
      weeksLater: 'in {count} weeks',
      monthsLater: 'in {count} months',
      yearsLater: 'in {count} years',
    },

    error: {
      invalidTimestamp: 'Invalid timestamp',
      invalidDatetime: 'Invalid date time format',
    },

    features: {
      title: 'Features',
      currentTimeDesc: 'Real-time display of current timestamp and date',
      bidirectional: 'Bidirectional',
      bidirectionalDesc: 'Convert timestamp to date and vice versa',
      multiFormat: 'Multi-format',
      multiFormatDesc: 'Supports seconds and milliseconds timestamps',
      copy: 'One-click Copy',
      copyDesc: 'Quickly copy conversion results',
    },
  },

  // SEO
  seo: {
    // Site-wide
    site: {
      title: 'DevToolkit - Online Developer Tools | JSON Formatter & Go Struct Converter & Timestamp',
      description: 'Free online developer toolkit: JSON formatter/parser with Postman-style table view, Go Struct to JSON converter, timestamp converter. Features inner JSON parsing, multi-tab, smart fix and more. No Postman installation needed.',
      keywords: 'JSON formatter,JSON parser,Postman JSON,online JSON tool,JSON table view,Postman style,Go Struct to JSON,JSON to Go Struct,timestamp converter,online dev tools,developer toolkit',
    },
    // JSON tool page
    json: {
      title: 'JSON Formatter - Postman-style Table View/Online Parser | DevToolkit',
      description: 'Powerful online JSON formatter with Postman-style table view: tree view, inner JSON parsing, multi-tab comparison, smart error fixing. No Postman installation needed, free online tool for developers.',
      keywords: 'JSON formatter,JSON parser,Postman JSON,JSON table view,Postman style,JSON tree view,JSON beautifier,JSON minifier,online JSON tool,JSON viewer,like Postman',
    },
    // Timestamp tool page
    timestamp: {
      title: 'Timestamp Converter - Unix Timestamp Online Tool | DevToolkit',
      description: 'Online timestamp converter: supports seconds/milliseconds timestamp to datetime conversion, multi-timezone support, real-time current timestamp display.',
      keywords: 'timestamp converter,Unix timestamp,online timestamp converter,date to timestamp,timestamp to date,milliseconds timestamp',
    },
    // Go Struct tool page
    gostruct: {
      title: 'Go Struct to JSON Converter - Online Go Struct Generator | DevToolkit',
      description: 'Online Go Struct and JSON bidirectional converter: Go struct to JSON example, JSON to Go struct generator, supports nested structs and multi-struct definitions. Essential for Go developers.',
      keywords: 'Go Struct to JSON,JSON to Go Struct,Go struct generator,Go JSON converter,Golang tools,Go development tools',
    },
    // Open Graph
    og: {
      siteName: 'DevToolkit - Developer Tools',
      type: 'website',
      imageAlt: 'DevToolkit - Online Developer Toolkit',
    },
    // Structured data
    structured: {
      appName: 'DevToolkit Developer Toolkit',
      appDescription: 'Free online developer toolkit providing JSON formatter, Go Struct converter, timestamp converter and more',
      appCategory: 'DeveloperApplication',
      features: {
        jsonFormatter: 'JSON Formatting & Parsing',
        jsonTable: 'JSON Table View',
        jsonTree: 'JSON Tree View',
        innerJson: 'Inner JSON Parsing',
        multiTab: 'Multi-Tab Support',
        smartFix: 'Smart Error Fixing',
        goStructConvert: 'Go Struct Bidirectional Conversion',
        timestampConvert: 'Timestamp Conversion',
      },
    },
  },

  // Go Struct Tool
  gostruct: {
    title: 'Go ↔ JSON',
    goToJson: 'Go → JSON',
    jsonToGo: 'JSON → Go',
    convert: 'Convert',
    swap: 'Swap',

    input: {
      goPlaceholder: 'Enter Go struct definition...',
      jsonPlaceholder: 'Enter JSON...',
      structName: 'Struct Name',
    },

    output: {
      jsonResult: 'JSON Result',
      goResult: 'Go Struct',
      copyResult: 'Copy Result',
    },

    options: {
      title: 'Options',
      omitempty: 'Add omitempty',
      inlineStruct: 'Inline nested structs',
      usePointer: 'Use pointer types',
    },

    history: {
      title: 'History',
      goHistory: 'Go Struct History',
      jsonHistory: 'JSON History',
    },

    error: {
      invalidGo: 'Invalid Go struct definition',
      invalidJson: 'Invalid JSON format',
      parseError: 'Parse Error',
    },

    features: {
      title: 'Features',
      tips: 'Tips',
      goToJsonDesc: 'Convert Go struct definition to corresponding JSON example',
      jsonToGoDesc: 'Auto-generate Go struct definition from JSON data',
      tagSupport: 'Tag Support',
      tagSupportDesc: 'Auto-generate json tags with omitempty option',
      nestedSupport: 'Nested Support',
      nestedSupportDesc: 'Handle complex nested struct conversions',
      typeInference: 'Type Inference',
      typeInferenceDesc: 'Infer appropriate Go types from JSON values',
      multiStruct: 'Multi-Struct',
      multiStructDesc: 'Define multiple related structs together',
    },

    examples: {
      simpleStruct: 'Simple Struct',
      nestedStruct: 'Nested Struct',
      arrayStruct: 'Array Struct',
    },
  },
};

export default en;
